<template>
  <div class="flex h-auto bg-gray-900 text-white">
    <!-- Sidebar -->
    <aside
      class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700"
    >
      <div class="flex items-center justify-center space-x-3">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20" />
      </div>
      <Navbar></Navbar>
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <!-- Header -->
      <header
        class="h-16 bg-gray-800 flex items-center justify-between px-6 shadow-md bg-gradient-to-b from-gray-800 to-gray-900 border-b border-gray-700"
      >
        <h2 class="text-xl font-semibold">Kubernetes</h2>
        <button
          @click="openCreateModal"
          class="bg-green-500 px-4 py-2 rounded hover:bg-green-600"
        >
          + Create Cluster
        </button>
      </header>

      <!-- Main Section -->
      <main class="p-6 flex-1">
        <div
          class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg"
        >
          <h3 class="text-lg font-semibold mb-4">Your Kubernetes Clusters</h3>

          <!-- Cluster Cards -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              v-for="cluster in clusters"
              :key="cluster.id"
              class="bg-gray-800 rounded-xl shadow-md p-5 border border-gray-700 hover:shadow-xl transform hover:-translate-y-1 transition"
            >
              <div class="flex justify-between items-center mb-3">
                <h4
                  class="text-lg font-semibold cursor-pointer hover:text-blue-400"
                  @click="viewCluster(cluster)"
                >
                  {{ cluster.name }}
                </h4>
                <span
                  :class="getStatusClass(cluster.status)"
                  class="text-sm font-medium"
                  >{{ cluster.status }}</span
                >
              </div>
              <p class="text-gray-400 text-sm">Nodes: {{ cluster.node_count }}</p>
              <p class="text-gray-400 text-sm">
                Price:
                <span class="text-green-400 font-semibold"
                  >${{ getClusterPrice(cluster) }}/mo</span
                >
              </p>
              <div class="flex justify-end mt-4">
                <button
                  @click="confirmDelete(cluster.id)"
                  class="bg-red-500 px-3 py-1 rounded hover:bg-red-600 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Create Cluster Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div class="bg-gray-800 p-6 rounded-xl w-1/3 shadow-lg">
        <h3 class="text-lg font-semibold mb-4">Create Kubernetes Cluster</h3>

        <input
          v-model="newCluster.name"
          placeholder="Cluster Name"
          class="p-2 rounded bg-gray-700 text-white w-full mb-2"
        />
        <input
          v-model.number="newCluster.node_count"
          type="number"
          placeholder="Node Count"
          class="p-2 rounded bg-gray-700 text-white w-full mb-2"
          min="1"
        />

        <!-- Pricing Preview -->
        <div class="bg-gray-700 p-3 rounded mb-4 text-sm text-gray-300">
          Estimated Price:
          <span class="text-green-400 font-semibold"
            >${{ getClusterPrice(newCluster) }}/mo</span
          >
        </div>

        <div class="flex justify-end">
          <button
            @click="closeCreateModal"
            class="bg-gray-600 px-4 py-2 rounded mr-2"
          >
            Cancel
          </button>
          <button
            @click="createCluster"
            class="bg-green-500 px-4 py-2 rounded"
          >
            Create
          </button>
        </div>
      </div>
    </div>

    <!-- Cluster Details Modal -->
    <div
      v-if="selectedCluster"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div class="bg-gray-800 p-6 rounded-xl w-1/3 shadow-lg">
        <h3 class="text-lg font-semibold mb-4">
          Cluster: {{ selectedCluster.name }}
        </h3>
        <p><strong>Nodes:</strong> {{ selectedCluster.node_count }}</p>
        <p>
          <strong>Status:</strong>
          <span :class="getStatusClass(selectedCluster.status)">{{
            selectedCluster.status
          }}</span>
        </p>
        <p>
          <strong>Price:</strong>
          <span class="text-green-400 font-semibold"
            >${{ getClusterPrice(selectedCluster) }}/mo</span
          >
        </p>
        <p><strong>Created At:</strong> {{ selectedCluster.created_at }}</p>
        <div class="flex justify-end mt-4">
          <button
            @click="selectedCluster = null"
            class="bg-gray-600 px-4 py-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :isOpen="showDeleteModal"
      title="Delete Cluster"
      message="Are you sure you want to delete this cluster? This action cannot be undone."
      @confirm="deleteCluster"
      @cancel="showDeleteModal = false"
    />

    <!-- Toast Notifications -->
    <ToastManager ref="toastManager" />
  </div>
</template>

<script>
import Navbar from "@/components/nav.vue";
import ConfirmModal from "@/components/ConfirmModal.vue";
import axios from "axios";
import ToastManager from "@/components/ToastManager.vue";

export default {
  name: "KubernetesPage",
  components: { Navbar, ToastManager, ConfirmModal },
  data() {
    return {
      clusters: [],
      newCluster: { name: "", node_count: 1 },
      showCreateModal: false,
      showDeleteModal: false,
      selectedCluster: null,
      clusterToDelete: null,
      pricePerNode: 10, // Example: $10 per node/month
    };
  },
  methods: {
    async fetchClusters() {
      try {
        const res = await axios.get(
          "https://api.syniac.com:3000/api/kubernetes/get",
          { withCredentials: true }
        );
        this.clusters = res.data;
      } catch (error) {
        this.$refs.toastManager.addToast("Error fetching clusters!", "error");
        console.error("Error fetching clusters:", error);
      }
    },
    async createCluster() {
      if (!this.newCluster.name || this.newCluster.node_count < 1) {
        this.$refs.toastManager.addToast(
          "Invalid cluster details!",
          "error"
        );
        return;
      }
      try {
        const res = await axios.post(
          "https://api.syniac.com:3000/api/kubernetes/add",
          this.newCluster,
          { withCredentials: true }
        );
        this.clusters.push(res.data);
        this.newCluster = { name: "", node_count: 1 };
        this.showCreateModal = false;
      } catch (error) {
        console.error("Error creating cluster:", error);
        this.$refs.toastManager.addToast(
          "Error creating cluster!",
          "error"
        );
      }
    },
    confirmDelete(id) {
      this.clusterToDelete = id;
      this.showDeleteModal = true;
    },
    async deleteCluster() {
      try {
        await axios.delete(
          `https://api.syniac.com:3000/api/kubernetes/delete/${this.clusterToDelete}`,
          { withCredentials: true }
        );
        this.clusters = this.clusters.filter(
          (cluster) => cluster.id !== this.clusterToDelete
        );
        this.showDeleteModal = false;
        this.$refs.toastManager.addToast(
          "Cluster deleted successfully!",
          "success"
        );
      } catch (error) {
        console.error("Error deleting cluster:", error);
        this.$refs.toastManager.addToast("Error deleting cluster!", "error");
      }
    },
    viewCluster(cluster) {
      this.selectedCluster = cluster;
    },
    openCreateModal() {
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    getStatusClass(status) {
      return {
        "text-green-500": status === "Running",
        "text-yellow-500": status === "Provisioning",
        "text-red-500": status === "Failed",
      };
    },
    getClusterPrice(cluster) {
      return cluster.node_count * this.pricePerNode;
    },
  },
  mounted() {
    this.fetchClusters();
    setInterval(this.fetchClusters, 10000);
  },
};
</script>
